import get from 'lodash/get';

import { File } from '../shared/models/file.model';

/* eslint-disable @typescript-eslint/naming-convention */
export enum FileType {
  code = 'code',
  csv = 'csv',
  tsv = 'tsv',
  dicom = 'dicom',
  embeddedhtml = 'embeddedhtml',
  image = 'image',
  markdown = 'markdown',
  markup = 'markup',
  montage = 'montage',
  nifti = 'nifti',
  obj = 'obj',
  pdf = 'pdf',
  text = 'text',
  video = 'video',
  metaio = 'ITK MetaIO Header',
}
/* eslint-enable @typescript-eslint/naming-convention */

/**
 * Get everything after the first dot in a filename, or the specified number of segments.
 */
export function getExtension(filename: string, segments = 0): string {
  const match = (filename || '').match(/\.([^?$]+)/);
  const extension = match && match[1];
  if (extension) {
    return extension.split('.').slice(-segments).join('.');
  }
  return null;
}

/**
 * Get the last part of a multi-part extension.
 */
export function getLastExtension(filename: string): string {
  return getExtension(filename, 1);
}

/**
 * Get the language of a file based on its extension.
 */
export function getLanguage(filename: string): string {
  const languages = {
    m: 'matlab',
    py: 'python',
    js: 'javascript',
    bvec: 'c',
    bval: 'c',
    bvecs: 'c',
    bvals: 'c',
    md: 'markdown',
    html: 'markup',
    xml: 'markup',
    toml: 'yaml',
    yml: 'yaml',
  };
  const extension = getLastExtension(filename);
  return languages[extension];
}

export function getViewerType(file: File): FileType {
  if (!file) {
    return null;
  }
  const fileType = (file.type || '').toLowerCase();
  const fileName = (file.name || '').toLowerCase();
  // Only handle files which can be opened without an external viewer
  switch (fileType) {
    case 'bval':
    case 'bvec':
      return FileType.code;
    case 'dicom':
      if (get(file, 'classification.Intent', []).includes('Non-Image')) {
        return null;
      }
      return FileType.dicom;
    case 'nifti':
      return FileType.nifti;
    case 'qa':
      switch (getLastExtension(fileName)) {
        case 'png':
          return FileType.image;
        case 'html':
          return FileType.markup;
        case 'json':
          return FileType.code;
        case 'zip':
          if (getExtension(fileName).includes('qa.html.zip')) {
            return FileType.embeddedhtml;
          }
          return null;
        default:
          return null;
      }
    case 'image':
      return FileType.image;
    case 'markup':
      return FileType.markup;
    case 'markdown':
      return FileType.markdown;
    case 'itk metaio header':
    case 'log':
    case 'text':
      return FileType.text;
    case 'pdf':
      return FileType.pdf;
    case 'source code':
      return FileType.code;
    case 'tabular data':
      switch (getExtension(fileName, 1)) {
        case 'csv':
          return FileType.csv;
        case 'tsv':
          return FileType.tsv;
        default:
          return null;
      }
    case 'video': {
      const extension = getExtension(fileName, 1);
      const allowedVideoExtensions = ['mp4', 'm4v', 'mov', 'ogg', 'webm']
      if (allowedVideoExtensions.includes(extension)) {
        return FileType.video;
      } else {
        return null;
      }
    }

  }
  switch (getExtension(fileName, 2)) {
    case 'html.zip':
      return FileType.embeddedhtml;
  }
  return null;
}
